import { ccv2Fetch, getCcv2TenantId } from './index';
import { getCcv2ApiBase, getCcv2Token } from './index';

/** @returns {Promise<Array>} metrics catalog entries */
export const fetchCatalog = (includeInactive = false) =>
  ccv2Fetch(`/v2/callcenter/metrics/catalog${includeInactive ? '?includeInactive=true' : ''}`);

/** @param {number} id @returns {Promise<Object>} */
export const fetchCatalogItem = (id) =>
  ccv2Fetch(`/v2/callcenter/metrics/catalog/${id}`);

/** @param {Object} dto @returns {Promise<Object>} */
export const createCatalogItem = (dto) =>
  ccv2Fetch('/v2/callcenter/metrics/catalog', { method: 'POST', body: JSON.stringify(dto) });

/** @param {number} id @param {Object} dto @returns {Promise<Object>} */
export const updateCatalogItem = (id, dto) =>
  ccv2Fetch(`/v2/callcenter/metrics/catalog/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });

/** @param {number} id @returns {Promise<null>} */
export const deleteCatalogItem = (id) =>
  ccv2Fetch(`/v2/callcenter/metrics/catalog/${id}`, { method: 'DELETE' });

/** Reseed default metrics @returns {Promise<Object>} */
export const seedDefaultMetrics = () =>
  ccv2Fetch('/v2/callcenter/metrics/catalog/seed-defaults');

// ── Targets ──────────────────────────────────────────────────────────────────

/** @param {number|undefined} metricId @param {string|undefined} scopeType @param {string|undefined} scopeId */
export const fetchTargets = (metricId, scopeType, scopeId) => {
  const params = new URLSearchParams();
  if (metricId) params.set('metricId', String(metricId));
  if (scopeType) params.set('scopeType', scopeType);
  if (scopeId) params.set('scopeId', scopeId);
  const q = params.toString();
  return ccv2Fetch(`/v2/callcenter/metrics/targets${q ? `?${q}` : ''}`);
};

/** @param {{ idMetricCatalog: number, scopeType: string, targetValue: number, warningValue?: number, criticalValue?: number }} dto */
export const createTarget = (dto) =>
  ccv2Fetch('/v2/callcenter/metrics/targets', { method: 'POST', body: JSON.stringify(dto) });

/** @param {number} id */
export const closeTarget = (id) =>
  ccv2Fetch(`/v2/callcenter/metrics/targets/${id}`, { method: 'DELETE' });

// ── Analytics KPIs ────────────────────────────────────────────────────────────

/**
 * Fetch aggregated KPIs (analytics service on port 4020, proxied through 4010).
 * @param {string} [agentKey] - if provided, fetch agent-specific KPIs
 */
export const fetchKpis = async (agentKey) => {
  const base = await getCcv2ApiBase();
  const tenantId = getCcv2TenantId();
  const token = getCcv2Token();
  const suffix = agentKey ? `/agents/${encodeURIComponent(agentKey)}` : '';
  const res = await fetch(`${base}/v2/analytics/metrics/${tenantId}/kpis${suffix}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

/**
 * Fetch queue-level metrics
 */
export const fetchQueueMetrics = async () => {
  const base = await getCcv2ApiBase();
  const tenantId = getCcv2TenantId();
  const token = getCcv2Token();
  const res = await fetch(`${base}/v2/analytics/metrics/${tenantId}/queues`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

/**
 * Fetch historical trend data
 * @param {{ startDate: string, endDate: string, granularity?: string }} params
 */
export const fetchHistory = async ({ startDate, endDate, granularity = 'daily' }) => {
  const base = await getCcv2ApiBase();
  const tenantId = getCcv2TenantId();
  const token = getCcv2Token();
  const url = `${base}/v2/analytics/history/${tenantId}/queues?start_date=${startDate}&end_date=${endDate}&granularity=${granularity}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};
